import { AddressV2, PersonalInformation } from '@shared/data-access/interfaces';

import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';

@Injectable({
	providedIn: 'root'
})
export class SavedAddressService {

	/**
	 * @description
	 * returns the current value of the primary address BehaviorSubject
	*/
	get primaryAddressId(): string {
		return this.primaryAddressIdSubject$.value;
	}

	/**
	 * @description
	 * Update BehaviorSubject with primary address id data
	 */
	set primaryAddressId(primaryAddressId: string) {
		this.primaryAddressIdSubject$.next(primaryAddressId);
	}
	/**
	 * @description
	 * returns the current value of the selected shipping address BehaviorSubject
	 */
	get selectedShippingAddress(): AddressV2 {
		return this.selectedShippingAddressSubject$.value;
	}

	/**
	 * @description
	 * Update BehaviorSubject with new selected shipping saved address data
	 */
	set selectedShippingAddress(address: AddressV2) {
		this.selectedShippingAddressSubject$.next(address);
	}

	/**
	 * @description
	 * returns the current value of the selected billing address BehaviorSubject
	 */
	get selectedBillingAddress(): AddressV2 {
		return this.selectedBillingAddressSubject$.value;
	}

	/**
	 * @description
	 * Update BehaviorSubject with new selected shipping saved address data
	 */
	set selectedBillingAddress(address: AddressV2) {
		this.selectedBillingAddressSubject$.next(address);
	}

	/**
	 * @description
	 * returns the current value of the saved addresses BehaviorSubject
	 */
	get savedAddresses(): AddressV2[]  {
		return this.savedAddresses$.value;
	}

	/**
	 * @description
	 * Updates BehaviorSubject with new saved addresses array
	 */
	set savedAddresses(savedAddresses: AddressV2[]) {
		this.savedAddresses$.next(savedAddresses);
	}

	/**
	 * @description
	 * returns the current value of the saved addresses BehaviorSubject without the primary address
	 */
	get savedAddressesWithoutPrimary(): AddressV2[]  {
		return this.savedAddresses$.value.filter((address) => address.addressId !== this.primaryAddressId);
	}

	private primaryAddressIdSubject$ = new BehaviorSubject<string>(null);
	private selectedShippingAddressSubject$ = new BehaviorSubject<AddressV2>(null);
	private selectedBillingAddressSubject$ = new BehaviorSubject<AddressV2>(null);
	private savedAddresses$ = new BehaviorSubject<AddressV2[]>([]);

	/**
	 * @description
	 * Takes the first/last name from the athlete's registered info and applies it to the primary address if necessary
	 */
	populatePrimaryFirstAndLastName(personalInfo: PersonalInformation): void {
		if (!this.savedAddresses || !this.savedAddresses.length || !personalInfo) return;

		this.savedAddresses = this.savedAddresses.map((savedAddress) => {
			// Ensure address names are trimmed so that empty form fields are found
			if (savedAddress.firstName) {
				savedAddress.firstName = savedAddress.firstName.trim();
			}
			if (savedAddress.lastName) {
				savedAddress.lastName = savedAddress.lastName.trim();
			}

			// If the saved address is the primary address and it doesn't have a first or last name
            // Then populate the first and last name using the athlete registered personal info
            // eslint-disable-next-line sonarjs/no-collapsible-if
            if (savedAddress.addressId === personalInfo.primaryAddress) {
                // Ensure primary name doesn't contain white space
                if (!savedAddress.firstName && !savedAddress.lastName) {
                    savedAddress.firstName = personalInfo.firstName ? personalInfo.firstName.trim() : '';
                    savedAddress.lastName = personalInfo.lastName ? personalInfo.lastName.trim() : '';
                }
            }
			return savedAddress;
		});
	}

	/**
	 * @description
	 * Find a saved address in the array by index and set it to selectedBillingAddress
	 */
	setSelectedBillingByIndex(savedAddressIndex: number): void {
		this.selectedBillingAddress = this.savedAddresses[savedAddressIndex];
	}

	/**
	 * @description
	 * Find a saved address in the array by index and set it to selectedShippingAddress
	 */
	setSelectedShippingByIndex(savedAddressIndex: number): void {
		this.selectedShippingAddress = this.savedAddresses[savedAddressIndex];
	}

	/**
	 * @description
	 * Add a new address to our saved addresses array if the address doesn't exists
	 */
	addSavedAddress(newAddress: AddressV2): void {
		// Ensure address is already in our array
		if (!newAddress || this.findSavedAddressIndexById(newAddress.addressId) > -1) return;
		const currentArray = this.savedAddresses;
        const updatedArray = [...currentArray, newAddress];
		this.savedAddresses = updatedArray;
	}

	/**
	 * @description
	 * Remove address from our saved addresses array
	 */
	removeSavedAddressById(addressId: string): void {
		if (!this.savedAddresses || !this.savedAddresses.length || !addressId) return;
		this.savedAddresses = this.savedAddresses.filter(savedAddress => savedAddress.addressId !== addressId);
	}

	/**
	 * @description
	 * Update saved address within our saved addresses array
	 */
	updateSavedAddressById(addressId: string, newAddress: AddressV2): void {
		// Ensure the savedAddresses array is not empty, that the new address is not null, the addressId is not null, and the addressId is not the same as the new address
		if (!this.savedAddresses?.length || !newAddress || !addressId || addressId === newAddress.addressId) return;

		// If the address is already in our array then we need to remove the old address
		if (this.findSavedAddressIndexById(newAddress.addressId) > -1) {
			this.removeSavedAddressById(addressId);
			this.moveAddressToTop(this.primaryAddressId);
			return;
		}
		// Otherwise we need to update the address in our array
		this.savedAddresses = this.savedAddresses.map((savedAddress) => {
			if (savedAddress.addressId === addressId) {
				return newAddress;
			}
			return savedAddress;
		});
	}

	/**
	 * @description
	 * Find a saved address in the array by index and move it to the top of the array
	 */
	moveAddressToTop(addressId: string): void {
		if (!this.savedAddresses || !this.savedAddresses.length || !addressId) return;
		const addresses = this.savedAddresses;

		// If the first saved address is the primary address then we're good to move forward
		if (addresses[0].addressId !== addressId) {
			// Otherwise we need to move the primary address to the front of the array
			const primaryAddressIndex = this.findSavedAddressIndexById(addressId);

			// Remove address from its current position
			if (primaryAddressIndex !== -1) {
				const primaryAddress = addresses.splice(primaryAddressIndex, 1)[0];
				addresses.unshift(primaryAddress);
				this.savedAddresses = addresses;
			}
		}
	}

	/**
	 * @description
	 * Find a saved address in the array and return its index
	 */
	findSavedAddressIndexById(addressId: string): number {
		return this.savedAddresses.findIndex((address) => address.addressId === addressId);
	}
}
